/**
 * Prefilled Decision Management System
 *
 * This module handles the lifecycle of decisions created from document uploads,
 * providing type safety, validation, and consistent data flow.
 */

export interface PrefillMetadata {
	sourceFiles: Array<{
		name: string;
		size: number;
		type: string;
		processingMethod: string;
	}>;
	tempFileIds?: number[]; // Add temp file IDs for new attachment system
	extractionMetadata: {
		method: string;
		textBlocks: number;
		tables: Array<any>;
		forms: Array<any>;
		processedAt: string;
		confidence?: number;
	};
	processingHistory: Array<{
		step: string;
		timestamp: string;
		success: boolean;
		error?: string;
	}>;
}

export interface PrefillDecisionData {
	// Core decision fields
	title: string;
	matter: string;
	description: string;
	summary: string;
	keywords: string;
	date: string;

	// Swedish document-specific fields
	unit?: string;
	validFrom?: string;
	validTo?: string;
	validityPeriod?: string;
	referenceNumber?: string;
	confidence?: number;

	// Extended fields for complex documents
	basisDescription?: string;
	decisionDescription?: string;

	// Document context
	documentType?: "contract" | "policy" | "meeting_minutes" | "report" | "other";
	extractedEntities?: Array<{
		type: "person" | "organization" | "date" | "amount" | "location";
		value: string;
		confidence: number;
	}>;

	// Suggested categorization
	suggestedCategory?: string;
	suggestedLabels?: string[];
	urgencyLevel?: "low" | "medium" | "high";

	// Metadata
	metadata: PrefillMetadata;

	// Temporary data for tabs
	tempAttachments?: Array<any>;
	tempQuestions?: Array<any>;
	tempDirections?: Array<any>;
	tempChunks?: Array<any>;
}

export enum PrefillStatus {
	PROCESSING = "processing",
	READY = "ready",
	ERROR = "error",
	SAVED = "saved",
}

export interface PrefillDecisionState {
	status: PrefillStatus;
	data: PrefillDecisionData | null;
	processingJobId?: string;
	error?: string;
	progress?: {
		stage: string;
		percentage: number;
		message: string;
	};
}

/**
 * Prefilled Decision Manager Class
 */
export class PrefillDecisionManager {
	private static readonly STORAGE_KEY = "prefillDecisionData";
	private static readonly TEMP_DATA_PREFIX = "tempDecision";

	/**
	 * Initialize a new prefilled decision from document upload
	 */
	static initializeFromDocument(file: File, extractedData: any): PrefillDecisionData {
		const now = new Date().toISOString();

		return {
			title: extractedData.title || "",
			matter: extractedData.matter || "",
			description: extractedData.description || extractedData.text || "",
			summary: extractedData.summary || "",
			keywords: extractedData.keywords || "",
			date: extractedData.date || "",

			// Swedish document-specific fields from enhanced extraction
			unit: extractedData.unit || "",
			validFrom: extractedData.validFrom || "",
			validTo: extractedData.validTo || "",
			validityPeriod: extractedData.validityPeriod || "",
			referenceNumber: extractedData.referenceNumber || "",
			confidence: extractedData.confidence || 0,

			basisDescription: extractedData.basisDescription || extractedData.text,
			decisionDescription: extractedData.decisionDescription || extractedData.description || extractedData.summary,

			documentType: this.detectDocumentType(extractedData.text, file.name),
			extractedEntities: extractedData.entities || [],

			suggestedCategory: extractedData.category || "",
			suggestedLabels: extractedData.labels || [],
			urgencyLevel: this.assessUrgency(extractedData.text),

			metadata: {
				sourceFiles: [
					{
						name: file.name,
						size: file.size,
						type: file.type,
						processingMethod: extractedData.method || "Textract Processing",
					},
				],
				extractionMetadata: {
					method: extractedData.method || "Textract Processing",
					textBlocks: extractedData.textBlocks || 0,
					tables: extractedData.tables || [],
					forms: extractedData.forms || [],
					processedAt: now,
					confidence: extractedData.confidence,
				},
				processingHistory: [
					{
						step: "Document Upload",
						timestamp: now,
						success: true,
					},
					{
						step: "Text Extraction",
						timestamp: now,
						success: true,
					},
				],
			},
		};
	}

	/**
	 * Save prefilled decision to storage
	 */
	static save(data: PrefillDecisionData): void {
		try {
			sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));

			// Mark as ready for form
			sessionStorage.setItem("prefillStatus", PrefillStatus.READY);
		} catch (error) {
			throw new Error("Failed to save decision data");
		}
	}

	/**
	 * Load prefilled decision from storage
	 */
	static load(): PrefillDecisionData | null {
		try {
			const stored = sessionStorage.getItem(this.STORAGE_KEY);
			if (!stored) return null;

			const parsed = JSON.parse(stored);
			// Validate the parsed data structure
			if (!parsed || typeof parsed !== "object") return null;

			return parsed;
		} catch (error) {
			console.error("Failed to load prefill decision data:", error);
			// Clear corrupted data
			sessionStorage.removeItem(this.STORAGE_KEY);
			return null;
		}
	}

	/**
	 * Update specific field in prefilled decision
	 */
	static updateField(field: keyof PrefillDecisionData, value: any): void {
		const current = this.load();
		if (current) {
			(current as any)[field] = value;
			this.save(current);
		}
	}

	/**
	 * Save temporary tab data (attachments, questions, etc.)
	 */
	static saveTempData(tabType: string, data: any): void {
		const key = `${this.TEMP_DATA_PREFIX}${tabType}`;
		sessionStorage.setItem(key, JSON.stringify(data));
	}

	/**
	 * Load temporary tab data
	 */
	static loadTempData(tabType: string): any[] {
		try {
			const key = `${this.TEMP_DATA_PREFIX}${tabType}`;
			const stored = sessionStorage.getItem(key);
			if (!stored) return [];

			const parsed = JSON.parse(stored);
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			console.error(`Failed to load temp data for ${tabType}:`, error);
			return [];
		}
	}

	/**
	 * Clear all prefilled decision data
	 */
	static clear(): void {
		sessionStorage.removeItem(this.STORAGE_KEY);
		sessionStorage.removeItem("prefillStatus");
		sessionStorage.removeItem("documentToastShown");

		// Clear temp data
		["Attachments", "Questions", "Directions", "Chunks"].forEach((tab) => {
			sessionStorage.removeItem(`${this.TEMP_DATA_PREFIX}${tab}`);
		});
	}

	/**
	 * Get current status
	 */
	static getStatus(): PrefillStatus {
		return (sessionStorage.getItem("prefillStatus") as PrefillStatus) || PrefillStatus.ERROR;
	}

	/**
	 * Check if we have valid prefilled data
	 */
	static hasValidData(): boolean {
		const data = this.load();
		return data !== null && this.getStatus() === PrefillStatus.READY;
	}

	// Helper methods for content analysis
	private static detectDocumentType(text: string, filename: string): PrefillDecisionData["documentType"] {
		const lowerText = (text || "").toLowerCase();
		const lowerFilename = filename.toLowerCase();

		if (lowerText.includes("contract") || lowerFilename.includes("contract")) return "contract";
		if (lowerText.includes("policy") || lowerFilename.includes("policy")) return "policy";
		if (lowerText.includes("meeting") || lowerText.includes("minutes")) return "meeting_minutes";
		if (lowerText.includes("report") || lowerFilename.includes("report")) return "report";

		return "other";
	}

	private static assessUrgency(text: string): PrefillDecisionData["urgencyLevel"] {
		const lowerText = (text || "").toLowerCase();

		if (lowerText.includes("urgent") || lowerText.includes("immediate") || lowerText.includes("asap")) {
			return "high";
		}
		if (lowerText.includes("soon") || lowerText.includes("priority")) {
			return "medium";
		}

		return "low";
	}
}
